import isNumber from 'lodash/isNumber';
import { adjustFontSizeToViewport } from 'src/app/core/helpers/dom/adjust-font-to-viewport';
import { translate } from 'src/app/project/features/translate/translate';
import { getFontFamily } from 'src/app/project/modules/white-label/white-label-utils/get-font-family';
import { TDashletValueBreakdownResponseData } from '../../dashboard.consts';
import { formatGraphNumericalValue } from './utils/format-graph-numerical-value';

export const getTotalCostPlugin = (
  response: TDashletValueBreakdownResponseData,
  currencyCode: string,
  unit: string,
  isExport: boolean,
  decimalPlaces: number,
): any => ({
  id: 'totalCost',
  afterDatasetsDraw(chart: any): void {
    const hasData = chart.data.datasets.some(
      (dataset) =>
        dataset.data &&
        dataset.data.length > 0 &&
        dataset.data.some((value) => isNumber(value) && value > 0),
    );

    if (!hasData) {
      return; // No data, so no need to draw total cost
    }

    const totalCostFormatted = calculateTotalCost(response, decimalPlaces, currencyCode, unit);
    const offset = isExport ? 50 : 10;
    const fontFamily = getFontFamily();
    const fontSize = setFontSize(chart, totalCostFormatted, fontFamily);

    const { ctx, chartArea } = chart;

    if (Math.min(chartArea.height, chartArea.width) < 120) {
      return; // No space to draw total cost
    }

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#1D2939';

    ctx.font = `${isExport ? fontSize * 3.5 : fontSize}px ${fontFamily}`;
    ctx.fillText(
      `${totalCostFormatted}`,
      (chartArea.left + chartArea.right) / 2,
      (chartArea.top + chartArea.bottom) / 2 + offset,
    );

    ctx.fillStyle = '#98A2B3';
    ctx.font = `${isExport ? 42 : 12}px ${fontFamily}`;
    ctx.fillText(
      translate('total_cost'),
      (chartArea.left + chartArea.right) / 2,
      (chartArea.top + chartArea.bottom) / 2 - offset,
    );

    ctx.restore();
  },
});

const PRECISION_FACTOR = 1e8;

export function sumCosts(values: number[]): number {
  return values.reduce(
    (total, value) => Math.round((total + value) * PRECISION_FACTOR) / PRECISION_FACTOR,
    0,
  );
}

function calculateTotalCost(
  response: TDashletValueBreakdownResponseData,
  decimalPlaces: number,
  currencyCode: string,
  unit: string,
): string {
  const costs = Object.values(response.data).map((value) => (isNumber(value) ? value : 0));

  return formatGraphNumericalValue(sumCosts(costs), decimalPlaces, currencyCode, unit);
}

function setFontSize(chart: any, text: string, fontFamily: string): number {
  const availableSpace = {
    height: chart.chartArea.height,
    width: chart.chartArea.width,
  };
  const graphToFreeSpaceRatio = 0.5;
  const maxFontSize = 21;

  const maxSpaceAvailable =
    Math.min(availableSpace.height, availableSpace.width) * graphToFreeSpaceRatio;

  return Math.min(adjustFontSizeToViewport(maxSpaceAvailable, text, fontFamily), maxFontSize);
}
